import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import RaceVisualizer from "./pages/RaceVisualizer";
import FindRacesPage from "./pages/FindRacesPage";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import About from "./pages/About";
import HowToUploadPage from "./pages/HowToUploadPage";
import RacesListPage from "./pages/RacesListPage";
import AuthPage from "./pages/AuthPage";
import UserProfilePage from "./pages/UserProfilePage";
import WahooCallbackPage from "./pages/WahooCallbackPage";
import HammerheadCallbackPage from "./pages/HammerheadCallbackPage";
import AdminDashboard from "./pages/AdminDashboard";
import { TermsOfService, EULA, PrivacyPolicy } from "./pages/LegalDocs";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import WelcomeProfileModal from "./components/WelcomeProfileModal";
import { userService } from "./services/userService";
import { useIdleTimer } from "./hooks/useIdleTimer";
import IdleTimeoutModal from "./components/IdleTimeoutModal";
import ComingSoonPage from "./pages/ComingSoonPage";
import MobileApp from "./pages/MobileApp";
import GroupRideDetailsPage from "./pages/GroupRideDetailsPage";

const AppContent: React.FC = () => {
  const { currentUser, userProfile, loading, refreshUserProfile, logout } =
    useAuth();
  const [showWelcome, setShowWelcome] = useState(false);

  const handleIdle = () => {
    if (currentUser) {
      logout();
    }
  };

  const { isPromptActive, stay } = useIdleTimer({
    onIdle: handleIdle,
    idleTime: 4080, // 68 minutes
    promptTime: 180, // 3 minutes
    enabled: !!currentUser,
  });

  // DEV/UAT BYPASS: Allow access without verification if in Dev/UAT mode AND using a test email
  const isDevOrUat = import.meta.env.MODE === "development" || import.meta.env.MODE === "uat";

  const emailLower = currentUser?.email?.toLowerCase() || "";
  const isTestUser =
    emailLower.includes("test") || emailLower.endsWith("@example.com");

  const isVerified = currentUser?.emailVerified || (isDevOrUat && isTestUser);

  useEffect(() => {
    // If logged in, not loading, IS VERIFIED, and profile is missing basic data (like firstName)
    if (!loading && currentUser && isVerified && (!userProfile || !userProfile.firstName)) {
      // Check Account Age: Has the user existed for a while?
      const metadata = currentUser.metadata;
      if (metadata && metadata.creationTime) {
        const creationTime = new Date(metadata.creationTime).getTime();
        const now = Date.now();

        // If account is older than 7 days, assume they have already seen/skipped the welcome flow.
        if (now - creationTime > 7 * 24 * 60 * 60 * 1000) {
          return;
        }
      }

      // Check local storage to see if they skipped it this session/browser
      const hasDismissed = localStorage.getItem(
        `raceLineIQ_welcome_dismissed_${currentUser.uid}`,
      );
      if (!hasDismissed) {
        setShowWelcome(true);
      }
    }
  }, [loading, currentUser, userProfile, isVerified]);

  const handleCloseWelcome = async (didSave: boolean) => {
    // Always set dismissed flag to true
    if (currentUser) {
      localStorage.setItem(
        `raceLineIQ_welcome_dismissed_${currentUser.uid}`,
        "true",
      );
    }

    setShowWelcome(false);
    if (didSave) {
      await refreshUserProfile();
    } else {
      // Create placeholder profile on skip if it doesn't exist at all
      if (currentUser && !userProfile) {
        try {
          await userService.updateUserProfile(currentUser.uid, {
            email: currentUser.email || "",
          });
          await refreshUserProfile();
        } catch (e) {
          console.error("Failed to dismiss welcome modal permanently", e);
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="h-screen bg-gray-900 flex items-center justify-center text-white">
        <div className="flex flex-col items-center">
          <svg
            className="animate-spin h-10 w-10 text-cyan-500 mb-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <span className="text-gray-400">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      {currentUser && isPromptActive && (
        <IdleTimeoutModal onStay={stay} onLogout={logout} promptTime={180} />
      )}
      <Routes>
        {/* Public Legal Routes */}
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/eula" element={<EULA />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />

        {/* Dedicated Auth Route (Full screen) */}
        <Route
          path="/auth"
          element={currentUser && isVerified ? <Navigate to="/" replace /> : <AuthPage />}
        />

        {/* Main Application Routing with Navbar & Footer */}
        <Route
          path="/*"
          element={
            <div className="flex flex-col min-h-screen">
              <Navbar />
              <main className="flex-grow">
                <Routes>
                  {/* Public App Routes */}
                  <Route path="/" element={<FindRacesPage />} />
                  <Route path="/ride/:rideId" element={<GroupRideDetailsPage />} />
                  <Route path="/race/:raceId/category/:categoryId" element={<RaceVisualizer />} />
                  <Route path="/race/:raceId" element={<RaceVisualizer />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/how-to-upload" element={<HowToUploadPage />} />

                  {/* Protected Routes */}
                  <Route
                    path="/profile"
                    element={
                      currentUser && isVerified ? (
                        <UserProfilePage />
                      ) : (
                        <Navigate to="/auth?returnTo=/profile" replace />
                      )
                    }
                  />
                  <Route
                    path="/races"
                    element={
                      currentUser && isVerified ? (
                        <RacesListPage />
                      ) : (
                        <Navigate to="/auth?returnTo=/races" replace />
                      )
                    }
                  />
                  <Route
                    path="/admin"
                    element={
                      currentUser && isVerified ? (
                        <AdminDashboard />
                      ) : (
                        <Navigate to="/auth?returnTo=/admin" replace />
                      )
                    }
                  />
                  <Route
                    path="/wahoo-callback"
                    element={
                      currentUser && isVerified ? (
                        <WahooCallbackPage />
                      ) : (
                        <Navigate to="/auth" replace />
                      )
                    }
                  />
                  <Route
                    path="/hammerhead-callback"
                    element={
                      currentUser && isVerified ? (
                        <HammerheadCallbackPage />
                      ) : (
                        <Navigate to="/auth" replace />
                      )
                    }
                  />

                  {/* Catch-all fallback */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </main>
              <Footer />
            </div>
          }
        />
      </Routes>

      {/* Ensure Welcome Modal only shows if user is actually into the app (Verified) */}
      <WelcomeProfileModal
        isOpen={showWelcome && !!isVerified}
        onClose={handleCloseWelcome}
      />
    </>
  );
};

const App: React.FC = () => {
  // Feature flag to lock the app down for pre-launch
  // Set VITE_COMING_SOON=true in your environment variables to activate this
  const isComingSoonEnv = import.meta.env.VITE_COMING_SOON === "true";
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Production Dark: Secret Bypass Logic (Easter Egg Mode)
  const params = new URLSearchParams(window.location.search);
  if (params.get('bypass') === 'lock') {
    localStorage.removeItem('bypass_coming_soon');
  }
  const hasBypass = localStorage.getItem('bypass_coming_soon') === 'true';
  const showComingSoon = isComingSoonEnv && !hasBypass;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Debugging log: check your browser's Developer Console (F12) to see this value
  console.log(
    "VITE_COMING_SOON is currently set to:",
    import.meta.env.VITE_COMING_SOON,
  );

  if (showComingSoon) {
    return <ComingSoonPage />;
  }

  return (
    <AuthProvider>{isMobile ? <MobileApp /> : <AppContent />}</AuthProvider>
  );
};

export default App;
